import { useEffect, useState } from 'react'

import type { TChatRating } from '@homepal/api-contract'

const LS_NUDGE_SHOWN_AT_KEY = 'chat_feedback_nudge_shown_at'

const COOLDOWN_MS = 24 * 60 * 60 * 1000

const MIN_COMPLETED_ANSWERS = 1

const canShowNudge = (): boolean => {
  try {
    const shownAt = Number(localStorage.getItem(LS_NUDGE_SHOWN_AT_KEY))

    return !shownAt || Date.now() - shownAt > COOLDOWN_MS
  } catch {
    return false
  }
}

const markNudgeShown = (): void => {
  try {
    localStorage.setItem(LS_NUDGE_SHOWN_AT_KEY, String(Date.now()))
  } catch {
    // localStorage unavailable
  }
}

interface NudgeMessage {
  role: 'user' | 'assistant'
  pending?: boolean
  messageId?: string
  rating?: TChatRating | null
  hydrated?: boolean
}

/**
 * Decides when the feedback nudge is shown and which answer it rates.
 * `target` is null whenever the nudge should not be rendered.
 */
export const useFeedbackNudge = <T extends NudgeMessage>(
  messages: T[],
  streaming: boolean
) => {
  const [shown, setShown] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  // Only answers produced in this session; a replayed chat is not a fresh
  // answer to rate.
  const completedAnswers = messages.filter(
    (m) => m.role === 'assistant' && !m.pending && m.messageId && !m.hydrated
  )
  const latestAnswer: T | undefined =
    completedAnswers[completedAnswers.length - 1]
  const anyRated = messages.some((m) => m.rating)

  useEffect(() => {
    if (shown || dismissed || streaming) return
    if (completedAnswers.length < MIN_COMPLETED_ANSWERS || anyRated) return
    if (!canShowNudge()) return

    setShown(true)
    markNudgeShown()
  }, [shown, dismissed, streaming, completedAnswers.length, anyRated])

  const visible = shown && !dismissed && !streaming && Boolean(latestAnswer)

  return {
    target: visible ? (latestAnswer ?? null) : null,
    dismiss: () => setDismissed(true)
  }
}
